import React from 'react';
import { Loader2, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

// Skeleton loading components for better perceived performance
export const SkeletonCard = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`}>
    <div className="p-4">
      <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-300 rounded w-1/2 mb-4"></div>
      <div className="h-8 bg-gray-300 rounded"></div>
    </div>
  </div>
);

export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
  <div className="animate-pulse">
    <div className="grid grid-cols-4 gap-4 mb-4">
      {Array.from({ length: columns }).map((_, i) => (
        <div key={i} className="h-4 bg-gray-300 rounded"></div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="grid grid-cols-4 gap-4 py-3">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div key={colIndex} className="h-3 bg-gray-200 rounded"></div>
        ))}
      </div>
    ))}
  </div>
);

// Alias for backward compatibility
export const TableSkeleton = SkeletonTable;
export const FinancialCardSkeleton = SkeletonCard;

export const SkeletonForm = () => (
  <div className="animate-pulse space-y-4">
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 rounded w-1/4"></div>
      <div className="h-10 bg-gray-200 rounded"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 rounded w-1/3"></div>
      <div className="h-10 bg-gray-200 rounded"></div>
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-gray-300 rounded w-1/2"></div>
      <div className="h-20 bg-gray-200 rounded"></div>
    </div>
    <div className="h-10 bg-gray-300 rounded w-1/4"></div>
  </div>
);

// Enhanced loading states with better UX
export const LoadingSpinner = ({ 
  size = 'md', 
  text = 'Loading...', 
  showText = true,
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Loader2 className={`animate-spin ${sizeClasses[size]} text-blue-600`} />
      {showText && (
        <span className="ml-2 text-gray-600">{text}</span>
      )}
    </div>
  );
};

// Progress indicators for multi-step processes
export const ProgressIndicator = ({ 
  currentStep, 
  totalSteps, 
  steps = [],
  className = '' 
}) => {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex justify-between text-sm text-gray-600 mb-2">
        <span>Step {currentStep} of {totalSteps}</span>
        <span>{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      {steps.length > 0 && (
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          {steps.map((step, index) => (
            <span 
              key={index}
              className={`${
                index < currentStep ? 'text-blue-600 font-medium' : 'text-gray-400'
              }`}
            >
              {step}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Status indicators for different states
export const StatusIndicator = ({ 
  status, 
  text, 
  showIcon = true,
  className = '' 
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'loading':
        return {
          icon: <Loader2 className="w-4 h-4 animate-spin" />,
          color: 'text-blue-600',
          bg: 'bg-blue-50',
          border: 'border-blue-200'
        };
      case 'success':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          color: 'text-green-600',
          bg: 'bg-green-50',
          border: 'border-green-200'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          color: 'text-red-600',
          bg: 'bg-red-50',
          border: 'border-red-200'
        };
      case 'warning':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          color: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200'
        };
      default:
        return {
          icon: null,
          color: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-200'
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={`inline-flex items-center px-3 py-2 rounded-lg border ${config.bg} ${config.border} ${className}`}>
      {showIcon && config.icon && (
        <span className={`mr-2 ${config.color}`}>
          {config.icon}
        </span>
      )}
      <span className={`text-sm font-medium ${config.color}`}>
        {text}
      </span>
    </div>
  );
};

// Inline loading state for buttons
export const LoadingButton = ({ 
  loading, 
  children, 
  loadingText = 'Loading...',
  disabled = false,
  className = '',
  ...props 
}) => {
  return (
    <button
      disabled={loading || disabled}
      className={`relative ${className} ${(loading || disabled) ? 'opacity-50 cursor-not-allowed' : ''}`}
      {...props}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
      <span className={loading ? 'opacity-0' : ''}>
        {children}
      </span>
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center text-sm">
          {loadingText}
        </span>
      )}
    </button>
  );
};

// Page-level loading state
export const PageLoading = ({ 
  title = 'Loading...', 
  subtitle = 'Please wait while we load your content',
  showProgress = false,
  progress = 0 
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center max-w-md mx-auto px-4">
      <div className="mb-6">
        <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-600 mb-6">{subtitle}</p>
      {showProgress && (
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}
    </div>
  </div>
);

// Empty state component
export const EmptyState = ({ 
  icon: Icon,
  title = 'No data found',
  description = 'There are no items to display at the moment.',
  action,
  actionText = 'Get started',
  className = '' 
}) => (
  <div className={`text-center py-12 ${className}`}>
    {Icon && (
      <div className="mx-auto w-16 h-16 text-gray-400 mb-4">
        <Icon className="w-full h-full" />
      </div>
    )}
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-600 mb-6 max-w-sm mx-auto">{description}</p>
    {action && (
      <button
        onClick={action}
        className="btn btn-primary"
      >
        {actionText}
      </button>
    )}
  </div>
);

// PERFORMANCE OPTIMIZATION: Fast skeleton loaders for common UI patterns
export const FastSkeletonCard = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-100 rounded-lg p-4 ${className}`}>
    <div className="flex items-center space-x-4">
      <div className="rounded-full bg-gray-200 h-10 w-10"></div>
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    </div>
  </div>
);

export const FastSkeletonList = ({ items = 5, className = '' }) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <FastSkeletonCard key={i} />
    ))}
  </div>
);

export const FastSkeletonGrid = ({ items = 6, className = '' }) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="animate-pulse bg-gray-100 rounded-lg p-4">
        <div className="h-32 bg-gray-200 rounded mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
      </div>
    ))}
  </div>
);

// PERFORMANCE: Smart loading component that shows content immediately if available
export const SmartLoader = ({ 
  loading, 
  data, 
  skeleton: SkeletonComponent = FastSkeletonCard,
  children,
  fallbackText = 'Loading...',
  className = ''
}) => {
  if (loading && (!data || (Array.isArray(data) && data.length === 0))) {
    return <SkeletonComponent className={className} />;
  }
  
  if (loading && data) {
    // Show data with loading overlay
    return (
      <div className={`relative ${className}`}>
        {children}
        <div className="absolute top-2 right-2">
          <LoadingSpinner size="sm" text="" showText={false} />
        </div>
      </div>
    );
  }
  
  return <div className={className}>{children}</div>;
};

export default LoadingSpinner;